<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import type { VideoEntry } from '../types';
import { useVideoThumbnails } from '../composables/useVideoThumbnails';
import GroupSelector from './GroupSelector.vue';

const props = defineProps<{
  video: VideoEntry;
  selected?: boolean;
}>();

const emit = defineEmits<{
  play: [id: string, name: string, segmented: boolean];
  delete: [id: string];
  select: [id: string];
}>();

const { loadThumbnail } = useVideoThumbnails();

const video = props.video;

function onToggleSelect(e: Event) {
  e.stopPropagation();
  emit('select', video.id);
}
const thumbId = `thumb-${video.id.replace(/[^a-zA-Z0-9]/g, '_')}`;
const canvasRef = ref<HTMLCanvasElement | null>(null);
const isHovered = ref(false);
const showDeleteModal = ref(false);
const showGroupSelector = ref(false);

function playVideo() {
  emit('play', video.id, video.name, !!video.segmented);
}

function openDeleteModal(e: Event) {
  e.stopPropagation();
  showDeleteModal.value = true;
}

function confirmDelete() {
  showDeleteModal.value = false;
  emit('delete', video.id);
}

function cancelDelete() {
  showDeleteModal.value = false;
}

function openGroupSelector(e: Event) {
  e.stopPropagation();
  showGroupSelector.value = true;
}

function formatSize(bytes?: number): string {
  if (!bytes) return '未知大小';
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
}

let loaded = false;
let observer: IntersectionObserver | null = null;

onMounted(() => {
  if (!canvasRef.value) return;
  observer = new IntersectionObserver((entries) => {
    if (loaded) return;
    for (const entry of entries) {
      if (entry.isIntersecting) {
        loaded = true;
        loadThumbnail(canvasRef.value!, `/video/${video.id}`);
        observer?.disconnect();
      }
    }
  }, { rootMargin: '200px' });
  observer.observe(canvasRef.value);
});

onBeforeUnmount(() => {
  observer?.disconnect();
});
</script>

<template>
  <div
    class="video-card"
    @click="playVideo"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <div class="thumb-wrapper">
      <canvas ref="canvasRef" :id="`cv-${thumbId}`"></canvas>
      <div class="thumb-overlay" :class="{ visible: isHovered }">
        <div class="play-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>
      <span v-if="video.segmented" class="badge-hls">HLS</span>
      <button class="delete-btn" @click="openDeleteModal" title="删除">
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
      </button>
    </div>
    <label class="select-checkbox" @click.stop="onToggleSelect">
      <input type="checkbox" :checked="props.selected" @change="onToggleSelect" />
      <span class="checkmark"></span>
    </label>
    <div class="card-body">
      <div class="name">
        <span v-if="video.segmented" class="indicator">🔄</span>
        <span v-if="video.isDuplicate" class="indicator">⚠️</span>
        {{ video.name }}
      </div>
      <div class="meta">
        <span>{{ formatSize(video.size) }}</span>
        <span v-if="video.mime_type" class="dot">·</span>
        <span v-if="video.mime_type">{{ video.mime_type }}</span>
      </div>
      <div v-if="video.playlists?.length" class="tags">
        <span v-for="pl in video.playlists" :key="pl.id" class="tag">{{ pl.name }}</span>
      </div>
      <div class="card-footer">
        <button class="manage-groups-btn" @click="openGroupSelector">
          <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          管理分组
        </button>
      </div>
    </div>

    <!-- 删除确认弹窗 -->
    <Teleport to="body">
      <div v-if="showDeleteModal" class="modal-backdrop" @click="cancelDelete">
        <div class="modal-dialog" @click.stop>
          <div class="modal-icon">
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" stroke-width="1.5">
              <circle cx="12" cy="12" r="10"/>
              <path d="M12 8v4M12 16h.01"/>
            </svg>
          </div>
          <h3 class="modal-title">确认删除</h3>
          <p class="modal-body">确定要删除 "<strong>{{ video.name }}</strong>" 吗？</p>
          <p class="modal-hint">此操作不可恢复。</p>
          <div class="modal-actions">
            <button class="modal-btn modal-btn-cancel" @click="cancelDelete">取消</button>
            <button class="modal-btn modal-btn-danger" @click="confirmDelete">删除</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- 分组管理弹窗 -->
    <Teleport to="body">
      <GroupSelector
        v-if="showGroupSelector"
        :file-id="video.id"
        @close="showGroupSelector = false"
      />
    </Teleport>
  </div>
</template>

<style scoped>
.video-card {
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  cursor: pointer;
  overflow: hidden;
  transition: all var(--transition-normal);
  animation: fadeInUp 0.4s ease-out both;
  position: relative;
}
.video-card:hover {
  border-color: var(--accent);
  transform: translateY(-4px);
  box-shadow: var(--shadow-elevated), var(--shadow-glow);
}

.thumb-wrapper {
  position: relative;
  width: 100%;
  aspect-ratio: 16/9;
  background: var(--bg-tertiary);
  overflow: hidden;
}

.thumb-wrapper canvas {
  width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform var(--transition-slow);
}
.video-card:hover .thumb-wrapper canvas {
  transform: scale(1.05);
}

.thumb-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-normal);
}
.thumb-overlay.visible { opacity: 1; }

.play-icon {
  color: white;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.5));
  transform: scale(0.8);
  transition: transform var(--transition-normal);
}
.thumb-overlay.visible .play-icon { transform: scale(1); }

.badge-hls {
  position: absolute;
  bottom: 8px;
  right: 8px;
  background: linear-gradient(135deg, var(--accent), #8b5cf6);
  color: white;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 4px;
  letter-spacing: 0.05em;
}

.delete-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  color: var(--text-primary);
  border: none;
  border-radius: 8px;
  width: 28px;
  height: 28px;
  cursor: pointer;
  opacity: 0;
  transition: all var(--transition-fast);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}
.video-card:hover .delete-btn { opacity: 1; }
.delete-btn:hover {
  background: var(--danger);
  transform: scale(1.1);
}

.select-checkbox {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  align-items: center;
  cursor: pointer;
  z-index: 1;
}
.select-checkbox input {
  display: none;
}
.checkmark {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(4px);
}
.select-checkbox input:checked + .checkmark {
  background: var(--accent);
  border-color: var(--accent);
}
.checkmark::after {
  content: '';
  display: block;
  width: 6px;
  height: 10px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) translate(-1px, -1px);
  opacity: 0;
  transition: opacity var(--transition-fast);
}
.select-checkbox input:checked + .checkmark::after {
  opacity: 1;
}
.select-checkbox:hover .checkmark {
  border-color: #fff;
}

.card-body {
  padding: 12px 14px;
}

.name {
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.indicator { margin-right: 2px; }

.meta {
  color: var(--text-muted);
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.dot { color: var(--text-muted); }

.tags {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.tag {
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 100px;
}

.card-footer {
  margin-top: 8px;
}

.manage-groups-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  transition: all var(--transition-fast);
}
.manage-groups-btn:hover {
  border-color: var(--accent);
  color: var(--accent);
  background: var(--accent-soft);
}

.tag {
  background: var(--accent-soft);
  color: var(--accent);
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 100px;
}

@media (max-width: 768px) {
  .video-card:hover {
    transform: none;
    box-shadow: none;
  }

  .video-card:active {
    transform: scale(0.98);
  }

  .thumb-overlay {
    opacity: 0 !important;
  }

  .delete-btn {
    opacity: 1;
    width: 32px;
    height: 32px;
    top: 6px;
    right: 6px;
  }

  .card-body {
    padding: 10px 12px;
  }

  .name {
    font-size: 14px;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    white-space: normal;
  }
}

/* ── 删除确认弹窗 ── */
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.15s ease-out;
}

.modal-dialog {
  background: var(--bg-elevated);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-xl);
  padding: 32px;
  width: 360px;
  max-width: 90vw;
  text-align: center;
  animation: fadeInUp 0.2s ease-out;
  box-shadow: var(--shadow-elevated);
}

.modal-icon {
  color: var(--danger);
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 8px;
}

.modal-body {
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
  margin-bottom: 4px;
  word-break: break-all;
}

.modal-body strong {
  color: var(--text-primary);
}

.modal-hint {
  color: var(--text-muted);
  font-size: 12px;
  margin-bottom: 24px;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: center;
}

.modal-btn {
  flex: 1;
  padding: 10px 20px;
  border-radius: var(--radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-fast);
  border: none;
}

.modal-btn-cancel {
  background: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}
.modal-btn-cancel:hover {
  border-color: var(--accent);
  color: var(--accent);
}

.modal-btn-danger {
  background: var(--danger);
  color: white;
}
.modal-btn-danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}

@media (max-width: 768px) {
  .modal-dialog {
    padding: 24px;
    width: 300px;
  }
}
</style>
